import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Info } from "lucide-react";
import { ASSISTANTS, type AssistantId } from "@workspace/assistants";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { BODY, ConnectMarks, INK, MUTED, PillButton } from "@/components/assistant/assistant-ui";

/** AI 2: how to add UnfoldWIN to Claude (or ChatGPT) as a custom connector. */
export function ConnectAssistantDialog({
  assistant,
  connectorUrl,
  onOpenChange,
}: {
  assistant: AssistantId | null;
  connectorUrl: string;
  onOpenChange: (open: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [assistant]);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const info = assistant ? ASSISTANTS[assistant] : null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(connectorUrl);
      setCopied(true);
    } catch {
      // Clipboard can be blocked (permissions, insecure origin); the link stays selectable.
    }
  }

  return (
    <Dialog open={assistant !== null} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-[rgba(47,41,37,0.28)]"
        className="max-h-[92dvh] max-w-[580px] gap-[18px] overflow-y-auto rounded-[28px] border-0 bg-white p-6 shadow-[0_24px_60px_rgba(28,25,23,0.25)] sm:rounded-[28px] sm:p-8 [&>button:last-child]:right-6 [&>button:last-child]:top-6 sm:[&>button:last-child]:right-8 sm:[&>button:last-child]:top-8"
      >
        {info && assistant && (
          <>
            <ConnectMarks assistant={assistant} />
            <DialogTitle className={`text-[26px] font-semibold leading-8 tracking-[-0.04em] ${INK}`}>Connect {info.name}</DialogTitle>
            <DialogDescription className={`text-[15px] leading-[22px] ${MUTED}`}>
              Add UnfoldWIN to {info.name} as a connector. It takes about a minute.
            </DialogDescription>

            <ol className="flex flex-col gap-[14px]">
              <Step n={1} title="Copy your connector link">
                <div className="mt-2 flex items-center gap-[10px] rounded-xl border border-[rgba(47,41,37,0.1)] bg-[#f8f6f2] py-2.5 pl-3.5 pr-2.5">
                  <code className={`min-w-0 flex-1 truncate font-sans text-sm font-medium ${INK}`}>{connectorUrl}</code>
                  <button
                    type="button"
                    onClick={() => void copy()}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-[10px] border border-[rgba(47,41,37,0.12)] bg-white px-2.5 py-1.5 text-xs font-semibold ${INK} transition-colors hover:bg-[#f8f6f2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40`}
                  >
                    {copied ? <Check className="size-[13px]" /> : <Copy className="size-[13px]" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </Step>
              {info.connectSteps.map((s, i) => (
                <Step key={s.title} n={i + 2} title={s.title}>
                  <p className={`mt-2 text-[13px] ${MUTED}`}>{s.detail}</p>
                </Step>
              ))}
            </ol>

            <div className="flex gap-[10px] rounded-[14px] bg-[#f8f6f2] p-3.5">
              <Info className={`mt-px size-[15px] shrink-0 ${MUTED}`} />
              <p className={`text-xs leading-[18px] ${MUTED}`}>{info.planNote}</p>
            </div>

            <div className="h-px bg-[rgba(47,41,37,0.08)]" />

            <div className="flex flex-wrap items-center justify-end gap-3">
              <PillButton variant="outline" onClick={() => onOpenChange(false)}>
                Done
              </PillButton>
              <a
                href={info.settingsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-[22px] bg-[#2f2925] px-5 py-[11px] text-sm font-semibold text-white transition-colors hover:bg-[#2f2925]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40"
              >
                Open {info.name} settings
                <ExternalLink className="size-[15px]" />
              </a>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-xl bg-[#e9e2f6] text-xs font-semibold text-[#6e53ac]">{n}</span>
      <div className={`min-w-0 flex-1 ${BODY}`}>
        <p className={`text-[15px] font-semibold ${INK}`}>{title}</p>
        {children}
      </div>
    </li>
  );
}
